import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Factory, RefreshCw, ClipboardList, PackageCheck } from "lucide-react";

const nf = (v) => (v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export default function ProductionDemand() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [sortBy, setSortBy] = useState("urgency");

  const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("de-DE"); } catch { return s; } };
  const urgencyCls = (d) => d == null ? "text-[var(--text-muted)]" : d < 0 ? "text-red-600 font-bold" : d <= 7 ? "text-amber-600 font-semibold" : "text-[var(--text)]";
  const dueLabel = (d) => d == null ? "" : d < 0 ? ` · ${Math.abs(d)}T überfällig` : d === 0 ? " · heute" : ` · in ${d}T`;
  const sortedRows = [...rows].sort((a, b) => {
    if (sortBy === "need") return b.need - a.need;
    const an = a.days_until, bn = b.days_until;
    if (an == null && bn == null) return b.need - a.need;
    if (an == null) return 1;
    if (bn == null) return -1;
    if (an !== bn) return an - bn;
    return b.need - a.need;
  });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/production/demand-plan"); setRows(data); }
    catch { toast.error("Bedarf konnte nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createOrder = async (r) => {
    if (!r.recipe_id) { toast.error("Keine Rezeptur für diese Sorte hinterlegt"); return; }
    setBusy(r.item_id);
    try {
      await api.post("/production", { recipe_id: r.recipe_id, planned_quantity: r.suggested_quantity });
      toast.success(`Produktionsauftrag angelegt: ${nf(r.suggested_quantity)} × ${r.name}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler beim Anlegen"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="page-production-demand">
      <PageHeader
        eyebrow="Produktion"
        title="Bedarfsplanung"
        description="Automatischer Produktionsbedarf aus offenen Bestellungen und Warenbestand. Bedarf = offene Bestellmengen + Mindestbestand − Bestand − laufende Produktion."
        actions={
          <div className="flex gap-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 rounded-full text-xs w-auto gap-1" data-testid="demand-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="urgency">Nach Dringlichkeit</SelectItem>
                <SelectItem value="need">Nach Bedarfsmenge</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-full" onClick={load} data-testid="btn-refresh-demand">
              <RefreshCw size={16} className="mr-1" /> Aktualisieren
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/produktion/planung")} data-testid="btn-goto-planung">
              <Factory size={16} className="mr-1" /> Manuelle Planung
            </Button>
          </div>
        }
      />

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--text-muted)]">Lade Bedarf…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[var(--text-muted)]" data-testid="demand-empty">
            <PackageCheck className="mx-auto mb-2 text-[var(--success)]" /> Kein Produktionsbedarf — der Bestand deckt alle offenen Bestellungen und Mindestbestände.
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="overline">Sorte</TableHead>
                    <TableHead className="overline">Wunschtermin</TableHead>
                    <TableHead className="overline text-right">Bestellungen</TableHead>
                    <TableHead className="overline text-right">Bestand</TableHead>
                    <TableHead className="overline text-right">Laufend</TableHead>
                    <TableHead className="overline text-right">Mindest</TableHead>
                    <TableHead className="overline text-right">Bedarf</TableHead>
                    <TableHead className="overline text-right">Vorschlag</TableHead>
                    <TableHead className="overline text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((r) => (
                    <TableRow key={r.item_id} data-testid={`demand-row-${r.sku}`}>
                      <TableCell>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-[10px] font-mono text-[var(--text-muted)]">
                          {r.sku}{r.order_count > 0 ? ` · ${r.order_count} Bestellung(en)` : ""}
                        </div>
                      </TableCell>
                      <TableCell className={`text-xs ${urgencyCls(r.days_until)}`} data-testid={`demand-due-${r.sku}`}>
                        {fmtDate(r.earliest_delivery)}{dueLabel(r.days_until)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{nf(r.demand)}</TableCell>
                      <TableCell className="text-right font-mono">{nf(r.stock)}</TableCell>
                      <TableCell className="text-right font-mono">{nf(r.running)}</TableCell>
                      <TableCell className="text-right font-mono">{nf(r.min_stock)}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-[var(--pop-pink)]">{nf(r.need)} {r.unit}</TableCell>
                      <TableCell className="text-right font-mono">
                        {r.recipe_id ? (
                          <>{nf(r.suggested_quantity)}<div className="text-[10px] text-[var(--text-muted)]">{r.batches} Charge(n)</div></>
                        ) : (
                          <span className="text-[10px] text-red-600">keine Rezeptur</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" disabled={!r.recipe_id || busy === r.item_id} onClick={() => createOrder(r)}
                                data-testid={`btn-create-prod-${r.sku}`}
                                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                          <ClipboardList size={14} className="mr-1" />{busy === r.item_id ? "…" : "Auftrag"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="md:hidden divide-y divide-[var(--border)]">
              {sortedRows.map((r) => (
                <div key={r.item_id} className="p-4" data-testid={`demand-card-${r.sku}`}>
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)] mb-1">{r.sku}</div>
                  <div className={`text-[10px] mb-2 ${urgencyCls(r.days_until)}`}>Wunschtermin: {fmtDate(r.earliest_delivery)}{dueLabel(r.days_until)}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div><div className="text-[var(--text-muted)]">Bestellungen</div><div className="font-mono">{nf(r.demand)}</div></div>
                    <div><div className="text-[var(--text-muted)]">Bestand</div><div className="font-mono">{nf(r.stock)}</div></div>
                    <div><div className="text-[var(--text-muted)]">Laufend</div><div className="font-mono">{nf(r.running)}</div></div>
                    <div><div className="text-[var(--text-muted)]">Mindest</div><div className="font-mono">{nf(r.min_stock)}</div></div>
                    <div><div className="text-[var(--text-muted)]">Bedarf</div><div className="font-mono font-bold text-[var(--pop-pink)]">{nf(r.need)}</div></div>
                    <div><div className="text-[var(--text-muted)]">Vorschlag</div><div className="font-mono">{r.recipe_id ? `${nf(r.suggested_quantity)} (${r.batches} Ch.)` : "—"}</div></div>
                  </div>
                  <Button size="sm" disabled={!r.recipe_id || busy === r.item_id} onClick={() => createOrder(r)}
                          data-testid={`btn-create-prod-m-${r.sku}`}
                          className="w-full rounded-full bg-[var(--pop-yellow)] text-black font-bold">
                    {busy === r.item_id ? "…" : "Produktionsauftrag anlegen"}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
